/**
 * Cart lines — PURE. `saleItems[] → saleItems[]`, and `saleItems[] → what a screen renders`.
 *
 * ## The mutation model, and why landmine 5 does not apply here
 *
 * §13.6's fifth landmine is that the POS mutates the cart BY ARRAY INDEX with a reversal on
 * top: `updateSaleData(…, isCart: true)` does `itemIndex = length - 1 - itemIndex` to undo the
 * POS cart's `reverseCartItems`, so a cart that sorts, groups or filters its lines differently
 * edits the wrong one.
 *
 * Nothing here reverses anything. Every function takes the sale's `saleItems` and returns a
 * NEW array, and the adapter writes that array back whole — so the index a caller passes is
 * always an index into the array it was given, and a screen may render in any order it likes
 * as long as it carries `CartLine.index` along. That is why `updateSaleData` is not lifted for
 * the kiosk: the shape it exists to work around is not reproduced.
 *
 * ## Identity
 *
 * `itemNumber` is assigned by the BACKEND, so a freshly punched line has none, and two lines of
 * the same item are told apart only by their customisation. `lineSignature` is that identity:
 * item + options + note. Adding the same configuration twice bumps a quantity instead of
 * leaving the customer with two identical rows to reason about.
 */
import type { CartLine } from "./types";

/** The catalog card a sale line came from: a variant's card is its parent Group. */
export function cardIdOf(line: any, itemsById: Record<string, any>): string {
  return (
    itemsById?.[line?.itemId]?.parentItemId ??
    line?.variantValues?.[0]?.parentItemId ??
    line?.itemId
  );
}

/**
 * What makes two lines the same thing.
 *
 * Options are sorted before hashing, so two lines that picked the same add-ons in a different
 * order are still one line. `baseQty` is part of it: one extra cheese and two extra cheese are
 * different products, not the same product twice.
 */
export function lineSignature(saleItem: any): string {
  const options = (saleItem?.itemOptionList ?? [])
    .map((option: any) => `${option?.optionSetId}:${option?.itemId}:${option?.baseQty ?? 1}`)
    .sort()
    .join("|");
  return `${saleItem?.itemId}||${options}||${saleItem?.note ?? ""}`;
}

/**
 * The line an incoming item should merge into, or `-1`.
 *
 * ⚠️ **A saved line never merges.** `isSavedItem` marks a line already sent to the kitchen;
 * the POS guards every repeat path on it (`checkRepeatItems` callers test
 * `!repetitiveItem?.isSavedItem`) because changing the quantity of a fired line is not the
 * same event as ordering another one. The kiosk submits once, so it never hits this — but the
 * POS will consume this module, and a rule that only holds for one caller is not a rule.
 *
 * Searches from the END: the most recent matching line is the one the customer means.
 */
export function findMergeableLine(saleItems: any[], saleItem: any): number {
  const signature = lineSignature(saleItem);
  for (let index = (saleItems?.length ?? 0) - 1; index >= 0; index -= 1) {
    const candidate = saleItems[index];
    if (candidate?.isSavedItem) continue;
    if (lineSignature(candidate) === signature) return index;
  }
  return -1;
}

/** Add an item, merging into an identical unsaved line when there is one. */
export function addLine(saleItems: any[] = [], saleItem: any): any[] {
  const index = findMergeableLine(saleItems, saleItem);
  if (index === -1) return [...saleItems, saleItem];

  const merged = {
    ...saleItems[index],
    quantity: (saleItems[index]?.quantity ?? 0) + (saleItem?.quantity ?? 1),
  };
  return saleItems.map((line, at) => (at === index ? merged : line));
}

/**
 * Set one line's quantity. `0` removes it.
 *
 * `itemPriceTotal` is kept in step because the POS's `updateQuantity` does, and an untouched
 * stale value is visible for the frame between the write and the library's recalculation.
 * Every other total is left to `calculateForSale` — §13.9's second constraint.
 */
export function setLineQuantity(saleItems: any[] = [], index: number, quantity: number): any[] {
  if (index < 0 || index >= saleItems.length) return saleItems;
  if (quantity <= 0) return saleItems.filter((_line, at) => at !== index);

  return saleItems.map((line, at) =>
    at === index ? { ...line, quantity, itemPriceTotal: (line?.unitPrice ?? 0) * quantity } : line,
  );
}

export function stepLine(saleItems: any[] = [], index: number, delta: number): any[] {
  const current = saleItems?.[index]?.quantity ?? 0;
  return setLineQuantity(saleItems, index, current + delta);
}

export function removeLine(saleItems: any[] = [], index: number): any[] {
  return setLineQuantity(saleItems, index, 0);
}

// ------------------------------------------------------------------------- projections

/**
 * `itemId → total quantity in the cart`, in ONE pass over the sale.
 *
 * This is what a catalog grid reads to badge its cards, so it is built once per sale change
 * and looked up per card — never a `find` per card, which is what turns a 200-item menu into
 * 200 scans of the cart on every keystroke.
 *
 * A variant line counts under BOTH its own id and its parent's, so a Group card shows the sum
 * of its variants while a variant card rendered directly shows only its own.
 */
export function quantityIndex(
  saleItems: any[] = [],
  itemsById: Record<string, any> = {},
): Map<string, number> {
  const index = new Map<string, number>();
  for (const line of saleItems) {
    const quantity = line?.quantity ?? 0;
    if (!quantity) continue;
    const itemId = line?.itemId;
    if (itemId) index.set(itemId, (index.get(itemId) ?? 0) + quantity);
    const cardId = cardIdOf(line, itemsById);
    if (cardId && cardId !== itemId) index.set(cardId, (index.get(cardId) ?? 0) + quantity);
  }
  return index;
}

/**
 * Stable React key for a line.
 *
 * Once the backend has assigned an `itemNumber` that is the identity. Before then the position
 * is all there is, so a screen MUST render lines in sale order for the fallback to be stable —
 * the same requirement `index` carries.
 */
export function lineKey(saleItem: any, index: number): string {
  return saleItem?.itemNumber != null
    ? `${saleItem.itemId}#${saleItem.itemNumber}`
    : `${saleItem?.itemId}@${index}`;
}

export function toCartLine(
  saleItem: any,
  index: number,
  itemsById: Record<string, any>,
  optionSetsById: Record<string, any> = {},
): CartLine {
  return {
    key: lineKey(saleItem, index),
    index,
    itemId: saleItem?.itemId,
    cardId: cardIdOf(saleItem, itemsById),
    itemNumber: saleItem?.itemNumber,
    name: saleItem?.punchedName || saleItem?.longName || saleItem?.shortName || "",
    quantity: saleItem?.quantity ?? 0,
    unitPrice: saleItem?.unitPrice ?? 0,
    lineTotal: saleItem?.itemSubTotal ?? saleItem?.itemPriceTotal ?? 0,
    variantValues: (saleItem?.variantValues ?? []).map((value: any) => ({
      name: value?.name ?? "",
      value: value?.value ?? "",
    })),
    options: (saleItem?.itemOptionList ?? []).map((option: any) => ({
      name: option?.itemName || option?.name || "",
      optionSetName:
        optionSetsById?.[option?.optionSetId]?.label || optionSetsById?.[option?.optionSetId]?.name || option?.optionSetName || undefined,
      quantity: option?.baseQty ?? 1,
      price: option?.unitPrice ?? 0,
      optionSetID: option?.optionSetId,
    })),
    note: saleItem?.note || undefined,
    isSavedItem: Boolean(saleItem?.isSavedItem),
    signature: lineSignature(saleItem),
  };
}

/** Every line, in sale order. Render order must match — see `lineKey`. */
export function toCartLines(
  saleItems: any[] = [],
  itemsById: Record<string, any> = {},
  optionSetsById: Record<string, any> = {},
): CartLine[] {
  return saleItems.map((saleItem, index) => toCartLine(saleItem, index, itemsById, optionSetsById));
}

/**
 * The lines belonging to one catalog card — what the "+/− on a customisable item" modal lists.
 *
 * For a Group that is every variant line under it; for a simple item, its own lines. Order is
 * sale order, so the modal reads in the order the customer built it.
 */
export function linesForCard(
  saleItems: any[] = [],
  cardId: string,
  itemsById: Record<string, any> = {},
  optionSetsById: Record<string, any> = {},
): CartLine[] {
  const lines: CartLine[] = [];
  saleItems.forEach((saleItem, index) => {
    if (saleItem?.itemId !== cardId && cardIdOf(saleItem, itemsById) !== cardId) return;
    lines.push(toCartLine(saleItem, index, itemsById, optionSetsById));
  });
  return lines;
}

export function isEmpty(saleItems: any[] = []): boolean {
  return saleItems.length === 0;
}

/** Total units in the cart — the badge on the "go to cart" control. */
export function totalQuantity(saleItems: any[] = []): number {
  return saleItems.reduce((sum, line) => sum + (line?.quantity ?? 0), 0);
}
